/* eslint-disable @typescript-eslint/no-explicit-any */
import PaginationContainer from '@components/common/PaginationContainer/PaginationContainer.component'
import { usePageContentGridScrollMaxHeight } from '@hooks/usePageContentGridScrollMaxHeight'
import { PageEventArgs } from '@syncfusion/ej2-react-grids'
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Table } from 'react-bootstrap'
import { v4 as uuid } from 'uuid'

type Column = {
  key: string
  label: string
  formatter?: (value: any, row: any) => React.ReactNode
  style?: React.CSSProperties
  sortable?: boolean
  headerClassName?: string
  headerTextAlign?: string
}

type SortConfig = {
  key: string | null
  direction: 'asc' | 'desc'
}

type ClaimSharedTableProps = {
  columns: Column[]
  data: any[]
  additionalRows?: (row: any) => React.ReactNode
  className?: string
  rowClassName?: (row: any) => string
  onRowClick?: (row: any) => void
  onHeaderClick?: (key: string) => void
  sortConfig?: SortConfig
  pageSize?: number
  activeTab?: 'pre-auth' | 'tracking' | 'claims'
  /** When true, tbody height is measured from remaining page content (default). */
  useDynamicPageScroll?: boolean
  /** When false, table uses normal layout without a scrollable tbody (default: true). */
  enableTableScroll?: boolean
  maxHeight?: string // Used when useDynamicPageScroll is false (default: calc(100vh - 385px))
}

const ClaimSharedTable: React.FC<ClaimSharedTableProps> = ({
  columns,
  data,
  additionalRows,
  className,
  rowClassName,
  onRowClick,
  onHeaderClick,
  sortConfig,
  pageSize = 10,
  activeTab,
  useDynamicPageScroll = true,
  enableTableScroll = true,
  maxHeight = 'calc(100vh - 385px)',
}) => {
  const scrollSectionRef = useRef<HTMLDivElement>(null)
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentPageSize, setCurrentPageSize] = useState<number>(pageSize ?? 10)

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * currentPageSize
    return data.slice(start, start + currentPageSize)
  }, [data, currentPage, currentPageSize])

  useEffect(() => {
    setCurrentPageSize(pageSize ?? 10)
    setCurrentPage(1)
  }, [activeTab, pageSize])

  useEffect(() => {
    setCurrentPageSize(pageSize ?? 10)
  }, [pageSize])

  const handlePaginationContainerPageChange = useCallback(
    (args: PageEventArgs): void => {
      const page = parseInt(args.currentPage as string, 10) || 1
      setCurrentPage(page)
    },
    [],
  )

  const handlePageSizeChange = useCallback((newPageSize: number): void => {
    setCurrentPageSize(newPageSize)
    setCurrentPage(1)
  }, [])

  const useScrollableTable = enableTableScroll && useDynamicPageScroll

  const scrollSectionStyle = usePageContentGridScrollMaxHeight(
    scrollSectionRef,
    [
      data.length,
      currentPage,
      currentPageSize,
      activeTab,
      paginatedData.length,
    ],
    {
      disabled: !useScrollableTable || data.length === 0,
      headerSelector: 'thead.table-claim-light',
      pagerSelector: '.claim-table-pagination',
    },
  )

  useEffect(() => {
    if (!enableTableScroll) return

    const wrapper = tableWrapperRef.current
    const tbody = wrapper?.querySelector(
      'tbody.scrollable-tbody',
    ) as HTMLElement | null
    if (!wrapper || !tbody) return

    const syncScrollbarWidth = (): void => {
      const width = tbody.offsetWidth - tbody.clientWidth
      wrapper.style.setProperty('--claim-table-scrollbar-width', `${width}px`)
    }

    syncScrollbarWidth()

    const resizeObserver = new ResizeObserver(syncScrollbarWidth)
    resizeObserver.observe(tbody)

    return () => resizeObserver.disconnect()
  }, [
    enableTableScroll,
    data.length,
    currentPage,
    currentPageSize,
    paginatedData.length,
  ])

  const tableBody = (
    <>
      <div ref={tableWrapperRef} className="claim-shared-table-wrapper">
        <Table
          hover
          responsive
          className={`claim-table mb-0 ${enableTableScroll ? '' : 'claim-table-static'} ${className || ''}`}
        >
          <thead className="table-claim-light">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key + uuid()}
                  style={{
                    ...col.style,
                    cursor: col.sortable ? 'pointer' : 'default',
                    ...(col.headerTextAlign
                      ? { 'text-align': col.headerTextAlign }
                      : {}),
                  }}
                  className={col.headerClassName || ''}
                  onClick={() =>
                    col.sortable && onHeaderClick && onHeaderClick(col.key)
                  }
                >
                  {col.label}
                  {col.sortable && sortConfig?.key === col.key && (
                    <span className="sort-icon">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={enableTableScroll ? 'scrollable-tbody' : undefined}
            style={
              enableTableScroll && !useDynamicPageScroll
                ? { maxHeight }
                : undefined
            }
          >
            {paginatedData.map((row) => (
              <React.Fragment key={row.id + uuid()}>
                <tr
                  className={rowClassName ? rowClassName(row) : ''}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{ cursor: 'pointer' }}
                >
                  {columns.map((col) => (
                    <td key={col.key + uuid()} style={col.style}>
                      {col.formatter
                        ? col.formatter(row[col.key], row)
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
                {additionalRows && additionalRows(row)}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="claim-table-pagination">
        <PaginationContainer
          totalRecordsCount={data.length}
          pageSize={currentPageSize}
          currentPage={currentPage}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageSizeChange={(size) => handlePageSizeChange(size)}
          onPageChange={(args) => handlePaginationContainerPageChange(args)}
        />
      </div>
    </>
  )

  if (useScrollableTable) {
    return (
      <div
        ref={scrollSectionRef}
        className="claim-table-scroll-section"
        style={scrollSectionStyle}
      >
        {tableBody}
      </div>
    )
  }

  return tableBody
}

export default React.memo(ClaimSharedTable)
